/**
 * @fileoverview API hooks for SKU alias listing and deletion.
 * @module features/sku-alias/api/aliases
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { z } from 'zod'

import apiClient from '@/lib/apiClient'
import {
  masterVariantIdSchema,
  skuAliasIdSchema,
  skuAliasSchema,
  skuAliasSourceSchema,
  type MasterVariantId,
  type SkuAlias,
  type SkuAliasId,
} from '@/features/sku-alias/schemas/skuAliasSchema'
import { skuResolutionStatusQueryKey } from '@/features/sku-alias/api/resolutionStatus'
import { NEEDS_REVIEW_QUERY_KEY, NEEDS_REVIEW_COUNT_QUERY_KEY } from '@/features/sku-alias/api/needsReview'

function unwrapAliasPayload(payload: unknown): unknown {
  let current = payload
  for (let i = 0; i < 2; i += 1) {
    if (current && typeof current === 'object' && 'data' in (current as Record<string, unknown>)) {
      const nested = (current as { data?: unknown }).data
      if (nested !== undefined) {
        current = nested
        continue
      }
    }
    break
  }
  return current
}

const createSkuAliasRequestSchema = z.object({
  aliasSlug: z.string().min(1),
  source: skuAliasSourceSchema,
  masterVariantId: masterVariantIdSchema,
})
export type CreateSkuAliasPayload = z.infer<typeof createSkuAliasRequestSchema>

/**
 * Delete alias by id.
 *
 * @param aliasId Identifier of alias to remove.
 * @example
 * ```ts
 * await deleteSkuAlias(skuAliasIdSchema.parse(99))
 * ```
 */
export async function deleteSkuAlias(aliasId: SkuAliasId): Promise<void> {
  const parsedId = skuAliasIdSchema.parse(aliasId)
  await apiClient.delete(`/sku-aliases/${parsedId}`)
}

/**
 * Create alias for a SKU.
 *
 * @param payload Alias creation payload.
 * @returns Newly created alias record.
 */
export async function createSkuAlias(payload: CreateSkuAliasPayload): Promise<SkuAlias> {
  const body = createSkuAliasRequestSchema.parse(payload)
  const response = await apiClient.post('/sku-aliases', body)
  const payloadBody = unwrapAliasPayload(response.data)
  return skuAliasSchema.parse(payloadBody)
}

/**
 * Mutation hook for alias deletion with list invalidation.
 *
 * @param masterVariantId Variant identifier used to scope invalidation.
 * @returns Mutation result for deleting aliases.
 * @example
 * ```ts
 * const mutation = useDeleteSkuAliasMutation(masterVariantId)
 * mutation.mutate(aliasId)
 * ```
 */
export function useDeleteSkuAliasMutation(
  masterVariantId: MasterVariantId | number | null | undefined
): UseMutationResult<void, Error, SkuAliasId> {
  const queryClient = useQueryClient()
  const normalizedId =
    typeof masterVariantId === 'number' && Number.isFinite(masterVariantId) ? masterVariantId : null
  return useMutation<void, Error, SkuAliasId>({
    mutationKey: ['sku-alias', 'delete', normalizedId],
    mutationFn: async (aliasId: SkuAliasId) => deleteSkuAlias(aliasId),
    onSuccess: async () => {
      if (normalizedId === null) return
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: skuResolutionStatusQueryKey(normalizedId),
        }),
        // Invalidate needs review queries since alias deletion affects unmatched listings
        queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_COUNT_QUERY_KEY }),
      ])
    },
  })
}

/**
 * Mutation hook for alias creation.
 *
 * @param masterVariantId Variant identifier used to scope invalidation.
 * @returns Mutation result for creating aliases.
 */
export function useCreateSkuAliasMutation(
  masterVariantId: MasterVariantId | number | null | undefined
): UseMutationResult<SkuAlias, Error, CreateSkuAliasPayload> {
  const queryClient = useQueryClient()
  const normalizedId =
    typeof masterVariantId === 'number' && Number.isFinite(masterVariantId) ? masterVariantId : null
  return useMutation<SkuAlias, Error, CreateSkuAliasPayload>({
    mutationFn: async payload => createSkuAlias(payload),
    onSuccess: async () => {
      if (normalizedId === null) return
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: skuResolutionStatusQueryKey(normalizedId),
        }),
        // Invalidate needs review queries since alias creation may affect unmatched listings
        queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: NEEDS_REVIEW_COUNT_QUERY_KEY }),
      ])
    },
  })
}
